import {
  checkDeleted,
  displayNameDescriptor,
  nameDescriptor,
  checkListDeleted,
} from 'pages/AutomationEngine/helpers';
import {
  ARCHIVED_RECORD_FOUND_ACTIONS,
  EXISTING_RECORD_FOUND_ACTIONS,
  OWNER_SUBTYPES,
} from './constants';
import type {
  CreateRelatedEntityDetails,
  OwnerSubtype,
  TargetCustomObjectOption,
} from './types';
import type {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { type CreateRelatedEntityStep } from '../actionSectionTypes';
import { VariableNameAndType } from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import { type Field } from 'pages/AutomationEngine/types';

const forApi = (step: CreateRelatedEntityStep) => {
  return {
    details: {
      ...step.details,
      automationsToStart: (step.details.automationsToStart || []).map(
        ({ value }) => value
      ),
      targetCustomObject: step.details.targetCustomObject?.value || null,
      targetVariable: step.details.targetVariable?.value || null,
      newEntityOwnerVariable:
        step.details.newEntityOwnerVariable?.value || null,
      contextEntityField: step.details.contextEntityField?.value || null,
      newEntityStage: step.details.newEntityStage?.value || null,
      newEntityOwnerRole: step.details.newEntityOwnerRole?.value || null,
      newEntityOwnerEmployees:
        step.details.newEntityOwnerEmployees?.map(({ value }) => value) || [],
    },
  };
};

type ApiObject = Omit<
  CreateRelatedEntityDetails,
  | 'targetCustomObject'
  | 'targetVariable'
  | 'contextEntityField'
  | 'newEntityStage'
  | 'newEntityOwnerRole'
  | 'newEntityOwnerEmployees'
  | 'automationsToStart'
  | 'newEntityOwnerVariable'
> & {
  targetCustomObject:
    | (TargetCustomObjectOption['data'] & { deleted?: boolean })
    | null;
  targetVariable: (VariableNameAndType & { deleted?: boolean }) | null;
  contextEntityField: (Field & { deleted?: boolean }) | null;
  newEntityStage: {
    id: string;
    name: string;
    deleted: boolean;
  } | null;
  newEntityOwnerRole: {
    id: string;
    name: string;
    deleted: boolean;
  } | null;
  newEntityOwnerEmployees: {
    id: string;
    displayName: string;
    deleted: boolean;
  }[];
  automationsToStart: {
    id: string;
    name: string;
    deleted: boolean;
    active: boolean;
  }[];
  newEntityOwnerVariable: {
    name: string;
    deleted: boolean;
    metadata?: Record<string, any>;
  };
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
): CreateRelatedEntityDetails => {
  const {
    targetCustomObject,
    targetVariable,
    contextEntityField,
    newEntityStage,
    newEntityOwnerRole,
    newEntityOwnerEmployees,
    automationsToStart,
    newEntityOwnerSubType,
    newEntityOwnerVariable,
    existingRecordFoundAction,
    archivedRecordFoundAction,
    ...rest
  } = apiObject;

  checkDeleted(
    messageDictionary,
    addErrorMessage,
    targetCustomObject,
    (obj: { objectName: string }) => obj.objectName
  );

  checkDeleted(
    messageDictionary,
    addErrorMessage,
    targetVariable,
    nameDescriptor
  );

  checkDeleted(
    messageDictionary,
    addErrorMessage,
    contextEntityField,
    displayNameDescriptor
  );

  checkDeleted(
    messageDictionary,
    addErrorMessage,
    newEntityStage,
    nameDescriptor
  );

  if (newEntityOwnerSubType === OWNER_SUBTYPES.ROUND_ROBIN_SPECIFIC_ROLE) {
    checkDeleted(
      messageDictionary,
      addErrorMessage,
      newEntityOwnerRole,
      nameDescriptor,
      messageDictionary.deletedRole
    );
  }
  if (
    newEntityOwnerSubType &&
    (
      [
        OWNER_SUBTYPES.SPECIFIC_TEAM_MEMBER,
        OWNER_SUBTYPES.ROUND_ROBIN_CHOOSE_TEAM_MEMBERS,
      ] as OwnerSubtype[]
    ).includes(newEntityOwnerSubType)
  ) {
    checkListDeleted(
      messageDictionary,
      addErrorMessage,
      newEntityOwnerEmployees,
      displayNameDescriptor,
      messageDictionary.teamMember
    );
  }
  checkListDeleted(
    messageDictionary,
    addErrorMessage,
    automationsToStart,
    nameDescriptor
  );

  automationsToStart?.forEach((automation) => {
    if (automation.active === false) {
      addErrorMessage(messageDictionary.inactiveAutomation);
    }
  });

  return {
    ...rest,
    newEntityOwnerSubType,
    targetCustomObject: targetCustomObject
      ? {
          label: targetCustomObject.objectName,
          value: targetCustomObject.id,
          deleted: targetCustomObject.deleted,
          data: targetCustomObject,
        }
      : null,
    targetVariable: targetVariable
      ? {
          label: targetVariable.name,
          value: targetVariable.name,
          deleted: targetVariable.deleted,
          meta: targetVariable,
        }
      : null,
    contextEntityField: contextEntityField
      ? {
          label: contextEntityField.displayName,
          value: contextEntityField.id,
          deleted: contextEntityField.deleted,
          data: contextEntityField,
        }
      : null,
    newEntityStage: newEntityStage
      ? {
          label: newEntityStage.name,
          value: newEntityStage.id,
          deleted: newEntityStage.deleted,
        }
      : null,
    newEntityOwnerRole: newEntityOwnerRole
      ? {
          label: newEntityOwnerRole.name,
          value: newEntityOwnerRole.id,
          deleted: newEntityOwnerRole.deleted,
        }
      : null,
    newEntityOwnerEmployees: (newEntityOwnerEmployees || []).map(
      ({ id, displayName, deleted, ...rest }) => ({
        value: id,
        label: displayName,
        deleted,
        data: { id, displayName, ...rest },
      })
    ),
    automationsToStart: (automationsToStart || []).map(
      ({ id, name, deleted, active }) => ({
        value: id,
        label: name,
        deleted,
        active,
      })
    ),
    newEntityOwnerVariable: newEntityOwnerVariable
      ? {
          label: newEntityOwnerVariable.name,
          value: newEntityOwnerVariable.name,
          deleted: newEntityOwnerVariable.deleted,
          meta: newEntityOwnerVariable.metadata,
        }
      : null,
    existingRecordFoundAction:
      existingRecordFoundAction ??
      EXISTING_RECORD_FOUND_ACTIONS.create_append_suffix,
    archivedRecordFoundAction:
      archivedRecordFoundAction ??
      ARCHIVED_RECORD_FOUND_ACTIONS.error_do_not_create,
  };
};

export const createRelatedEntityDTO = {
  forApi,
  forFlow,
};
